import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { AuditCreation, IQuestionNode } from '@ifs-gcp/core';
import type { QuestionAnswer } from '@ifs-gcp/core/models';

import { getPlaceholderValue } from '@/components/audit/question/utils';
import styles from '@/components/question/question/question.module.scss';
import { ANSWER_COMPONENTS } from '@/features/audit/audit-elements/question-factory';
import type { AnswerProps } from '@/types/question';

type AuditQuestionElementProps = {
  element: IQuestionNode;
  domain: AuditCreation;
  moduleIdsInUse: string[];
};

export const AuditCreationQuestion = observer(
  ({ element, domain, moduleIdsInUse }: AuditQuestionElementProps) => {
    const {
      i18n: { language },
    } = useTranslation();

    const {
      props: { id, placeholder, readOnlyCondition, disabledCondition, context, options, revision },
    } = element;

    const setAnswer = React.useCallback(
      (value: QuestionAnswer) => {
        for (const ctxModuleId of context) {
          const module = domain.modulesHandler.modules.get(ctxModuleId);
          if (module) {
            module.questions.set(id, { answer: value, revision });
          }
        }
      },
      [domain, context, id, revision],
    );

    const moduleId = context[0];

    const Component = ANSWER_COMPONENTS[element.props.variant] as React.FC<
      AnswerProps<QuestionAnswer>
    >;

    if (!Component) {
      return null;
    }

    const processedOptions = options?.map((option) => ({
      ...option,
      disabledCondition:
        (option.disabledCondition && domain.conditionHandler.validate(option.disabledCondition)) ||
        moduleIdsInUse.includes(option.value),
    }));

    const answer: QuestionAnswer =
      (moduleId
        ? domain.modulesHandler.modules.get(moduleId)?.questions.questions.get(id)?.answer
        : []) || [];

    return (
      <div className={styles.question}>
        <Component
          answer={answer}
          question={{
            ...element,
            // @ts-expect-error fff
            props: {
              ...element.props,
              readOnlyCondition:
                readOnlyCondition && domain.conditionHandler.validate(readOnlyCondition),
              disabledCondition:
                disabledCondition && domain.conditionHandler.validate(disabledCondition),
              options: processedOptions,
              placeholder: getPlaceholderValue(placeholder, language, domain.conditionHandler),
            },
          }}
          language={language}
          onChange={setAnswer}
          onAddInput={() => {}}
          onRemoveInput={() => {}}
        />
      </div>
    );
  },
);
